/*
  Таблица «Должники» экрана обзвона - вариант Б, решение владельца 06.10.2026.

  Человек отмечает должников галочками и одной полосой внизу решает, позвонить
  им или написать. Отметка «можно звонить» стоит в каждой строке до выбора, а не
  после запуска: узнавать, что половине отмеченных звонить нельзя, из окна
  подтверждения - поздно, отмечать придётся заново.

  «Можно звонить» значит «обзвон его наберёт». Ночью это не меняется: обзвон
  ждёт утра сам, и что сейчас вне разрешённых часов, говорит одна строка над
  таблицей, а не «нельзя» в каждой строке. Всё это считает сервер.

  Компонент только рисует: что загружено и что выбрано - в `useDebtorList`.
*/

import { Link } from 'react-router-dom'

import { dateTime, money } from '../api/format.js'
import { useAccess } from './AccessContext.jsx'
import { Check, Empty, Field, Loading, Notice, Switch, Tag } from './Common.jsx'
import PageBar from './PageBar.jsx'
import PickShown from './PickShown.jsx'
import PickWhole from './PickWhole.jsx'

const VIEWS = [
  { value: 'all', title: 'Все с телефоном' },
  { value: 'pretrial', title: 'Досудебная работа' },
  { value: 'callable', title: 'Можно звонить' },
]

const EMPTY = {
  all: 'Здесь появятся должники, у которых известен телефон. Телефон берётся из сведений о должнике: карточка счёта, вкладка «Должники».',
  pretrial:
    'Досудебной работы сейчас нет ни по одному счёту с телефоном. Счёт попадает сюда, когда по нему начинается работа с должником.',
  callable:
    'Сейчас обзвон не наберёт никого: у каждого должника есть запрет, пауза или исчерпан предел обращений. Причина - в колонке «Звонок» на вкладке «Все с телефоном».',
}

function Row({ row, picked, onToggle }) {
  return (
    <tr>
      <td className="table__control">
        <Check
          checked={picked}
          onChange={() => onToggle(row)}
          title={`Выбрать счёт ${row.number}`}
        />
      </td>
      <td>
        <Link to={`/accounts/${row.account_id}`}>№ {row.number}</Link>
        <div className="muted">{row.address}</div>
      </td>
      <td data-label="Должник">
        {row.debtor_name ?? 'имя не указано'}
        <div className="muted">{row.phone}</div>
      </td>
      <td className="table__num" data-label="Долг">
        {money(row.claim_amount)}
      </td>
      <td data-label="Стадия">{row.stage_title}</td>
      <td className="table__block" data-label="Звонок">
        {row.can_call ? (
          <Tag kind="calm">можно звонить</Tag>
        ) : (
          <>
            <Tag kind="warn">звонить нельзя</Tag>
            <div className="muted">{row.refusal}</div>
          </>
        )}
      </td>
    </tr>
  )
}

export default function DebtorTable({ debtors, onCall, onSms }) {
  const { list, error, view, setView, bar, typed, setTyped, chosen, toggle, toggleAll, clear } =
    debtors
  /*
    21.10.2026: страницы режет сервер, вместе с переключателем и поиском - они работают над
    ВСЕМ списком, а не над показанной страницей. Отметки хранятся в `useDebtorList` строками
    и от страницы не зависят; «выбрать всех» в шапке берёт ровно показанную страницу - то же
    правило «выбираются показанные», что было и раньше. Весь отбор - кнопкой «Выбрать весь
    отбор» над таблицей (решение владельца 23.10.2026).
  */
  /*
    Лицензия не оплачена - звонить и писать нельзя, и сервер откажет (ревью 30.10.2026):
    кнопки видны, но выключены, как «Позвонить» в карточке счёта. Выбор остаётся рабочим.
  */
  const { readonly } = useAccess()
  const rows = list ? list.rows : []
  const picked = new Set(chosen.map((row) => row.account_id))
  const shownPicked = rows.filter((row) => picked.has(row.account_id)).length
  const options = VIEWS.map((item) => ({
    value: item.value,
    title: list ? `${item.title} · ${list.counts[item.value] ?? 0}` : item.title,
  }))

  return (
    <div className="card">
      <div className="section__title">Должники</div>
      <div className="row">
        <Switch value={view} onChange={setView} options={options} />
        <Field label="Поиск" width="l">
          <input
            className="field__input"
            type="search"
            placeholder="номер счёта или фамилия"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
          />
        </Field>
      </div>

      <Notice error={error} />
      {!list && !error ? <Loading what="Загружаем должников" /> : null}
      {list?.channel_blocked ? (
        <Notice kind="warn">
          <div className="notice__what">Звонить сейчас нечем.</div>
          <div className="notice__do">
            {list.channel_blocked} Сообщения отправлять это не мешает.
          </div>
        </Notice>
      ) : null}
      {list?.resume_at ? (
        <div className="muted">
          Сейчас вне разрешённых часов. Обзвон, запущенный сейчас, начнёт звонить{' '}
          {dateTime(list.resume_at)} по вашему времени.
        </div>
      ) : null}

      {list && rows.length === 0 ? (
        <Empty title={typed.trim() ? 'Никого не нашлось' : 'Должников здесь нет'}>
          {typed.trim()
            ? 'Поиск идёт по номеру лицевого счёта и фамилии. Проверьте написание или переключитесь на «Все с телефоном».'
            : EMPTY[view]}
        </Empty>
      ) : null}

      {/* Весь отбор - с сервера, той же частью списка и поиском (решение владельца 23.10.2026). */}
      <PickWhole
        total={list?.total ?? 0}
        shown={rows.length}
        filterKey={debtors.filterKey}
        fetchAll={debtors.fetchAll}
        onPicked={debtors.addAll}
        selected={chosen.length}
      />

      <PickShown
        count={rows.length}
        picked={shownPicked}
        onToggle={(on) => toggleAll(rows, on)}
      />

      {rows.length ? (
        <div className="table-scroll">
          <table className="table table--fixed table--debtors">
            <thead>
              <tr>
                <th className="table__control">
                  <Check
                    checked={shownPicked === rows.length}
                    indeterminate={shownPicked > 0 && shownPicked < rows.length}
                    onChange={(on) => toggleAll(rows, on)}
                    title="Выбрать показанных должников"
                  />
                </th>
                <th>Счёт</th>
                <th>Должник</th>
                <th className="table__num">Долг</th>
                <th>Стадия</th>
                <th>Звонок</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <Row
                  key={row.account_id}
                  row={row}
                  picked={picked.has(row.account_id)}
                  onToggle={toggle}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <PageBar {...bar} what="должников" />

      {chosen.length ? (
        <div className="pick-bar">
          <b>Выбрано: {chosen.length}</b>
          <button
            type="button"
            className="button button--quiet"
            disabled={readonly}
            onClick={onCall}
          >
            Позвонить
          </button>
          <button
            type="button"
            className="button button--quiet"
            disabled={readonly}
            onClick={onSms}
          >
            Отправить СМС
          </button>
          <button type="button" className="button button--quiet" onClick={clear}>
            Снять выбор
          </button>
        </div>
      ) : null}
    </div>
  )
}
